import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import ActionPanel from "./ActionPanel";
import ActionModal from "./ActionModal";
import CreateGameForm from "./CreateGameForm";
import CreateEntity, { CreateEntityField } from "./CreateEntity";
import GameCard from "./GameCard";
import { authenticatedFetch, API_BASE_URL } from "../utils/api";
import { sortByTimestampDescending } from "../shared/helper";
import type {
  AdminAction,
  AdminGame,
  AdminMessage,
  AdminPlayer,
  AdminTeam,
} from "../types/admin";

type PanelView = "createGame" | "createAdmin" | null;

interface AdminActionsProps {
  adminId: string;
  onActiveGameChange?: (gameId: string | null) => void;
  onLogout?: () => void;
}

interface DashboardState {
  games: Record<string, AdminGame>;
  teamsByGame: Record<string, AdminTeam[]>;
  playersById: Record<string, AdminPlayer>;
  playerIdsByTeam: Record<string, string[]>;
  messagesByGame: Record<string, AdminMessage[]>;
  actionsByGame: Record<string, AdminAction[]>;
  activeGameId: string | null;
  playerActionCounts: Record<string, number>; // player_id -> action_count
  playerCharCounts: Record<string, number>; // player_id -> chars_sent
  playerLandingSiteInvestments: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>; // player_id -> landing site data
}

const initialState: DashboardState = {
  games: {},
  teamsByGame: {},
  playersById: {},
  playerIdsByTeam: {},
  messagesByGame: {},
  actionsByGame: {},
  activeGameId: null,
  playerActionCounts: {},
  playerCharCounts: {},
  playerLandingSiteInvestments: {},
};

type DashboardAction =
  | { type: "setGameList"; games: AdminGame[] }
  | { type: "setActiveGame"; gameId: string | null }
  | { type: "upsertGame"; game: AdminGame }
  | { type: "upsertTeam"; team: AdminTeam }
  | { type: "mergePlayers"; players: AdminPlayer[] }
  |     {
      type: "hydrateGame";
      payload: {
        game: AdminGame;
        teams: AdminTeam[];
        players: AdminPlayer[];
        messages: AdminMessage[];
        actions: AdminAction[];
        playerActionCounts?: Record<string, number>;
        playerCharCounts?: Record<string, number>;
        playerLandingSiteInvestments?: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>;
      };
    }
  | { type: "upsertMessage"; gameId: string; message: AdminMessage }
  | { type: "upsertAction"; gameId: string; action: AdminAction }
  | { type: "setPlayerActionCount"; playerId: string; actionCount: number }
  | { type: "setPlayerActionCounts"; counts: Record<string, number> }
  | { type: "setPlayerCharCounts"; counts: Record<string, number> }
  | { type: "updatePlayerLandingSiteInvestments"; playerId: string; total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> };

function mergePlayersIntoState(
  state: DashboardState,
  players: AdminPlayer[]
): Pick<DashboardState, "playersById" | "playerIdsByTeam"> {
  if (players.length === 0) {
    return {
      playersById: state.playersById,
      playerIdsByTeam: state.playerIdsByTeam,
    };
  }

  const playersById = { ...state.playersById };
  const playerIdsByTeam = { ...state.playerIdsByTeam };

  players.forEach((player) => {
    const existing = playersById[player.player_id];

    // If the player moved teams, remove from the old team list first
    if (existing && existing.team_id !== player.team_id) {
      const previousTeamIds = playerIdsByTeam[existing.team_id] || [];
      playerIdsByTeam[existing.team_id] = previousTeamIds.filter(
        (id) => id !== player.player_id
      );
    }

    playersById[player.player_id] = {
      ...(existing || {}),
      ...player,
    };

    const teamId = player.team_id;
    const existingIds = playerIdsByTeam[teamId] || [];
    if (!existingIds.includes(player.player_id)) {
      playerIdsByTeam[teamId] = [...existingIds, player.player_id];
    } else {
      playerIdsByTeam[teamId] = existingIds;
    }
  });

  return { playersById, playerIdsByTeam };
}

function hydrateGameState(
  state: DashboardState,
  payload: {
    game: AdminGame;
    teams: AdminTeam[];
    players: AdminPlayer[];
    messages: AdminMessage[];
    actions: AdminAction[];
    playerActionCounts?: Record<string, number>;
    playerCharCounts?: Record<string, number>;
    playerLandingSiteInvestments?: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>;
  }
): DashboardState {
  const { game, teams, players, messages, actions, playerActionCounts, playerCharCounts, playerLandingSiteInvestments } = payload;
  const games = { ...state.games, [game.game_id]: game };
  const teamsByGame = { ...state.teamsByGame, [game.game_id]: teams };

  const playersById = { ...state.playersById };
  const playerIdsByTeam = { ...state.playerIdsByTeam };

  const teamIds = teams.map((team) => team.team_id);
  teamIds.forEach((teamId) => {
    const existingIds = playerIdsByTeam[teamId] || [];
    existingIds.forEach((playerId) => {
      if (playersById[playerId]?.team_id === teamId) {
        delete playersById[playerId];
      }
    });
    delete playerIdsByTeam[teamId];
  });

  players.forEach((player) => {
    playersById[player.player_id] = player;
    if (!playerIdsByTeam[player.team_id]) {
      playerIdsByTeam[player.team_id] = [];
    }
    if (!playerIdsByTeam[player.team_id].includes(player.player_id)) {
      playerIdsByTeam[player.team_id] = [...playerIdsByTeam[player.team_id], player.player_id];
    }
  });

  const messagesByGame = {
    ...state.messagesByGame,
    [game.game_id]: sortByTimestampDescending(messages, (msg) => msg.timestamp),
  };

  const actionsByGame = {
    ...state.actionsByGame,
    [game.game_id]: sortByTimestampDescending(actions, (action) => action.timestamp),
  };

  return {
    ...state,
    games,
    teamsByGame,
    playersById,
    playerIdsByTeam,
    messagesByGame,
    actionsByGame,
    activeGameId: game.game_id,
    playerActionCounts: playerActionCounts || state.playerActionCounts,
    playerCharCounts: playerCharCounts || state.playerCharCounts,
    playerLandingSiteInvestments: playerLandingSiteInvestments || state.playerLandingSiteInvestments,
  };
}

function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case "setGameList": {
      const games = { ...state.games };
      action.games.forEach((game) => {
        games[game.game_id] = game;
      });
      return { ...state, games };
    }
    case "setActiveGame":
      return { ...state, activeGameId: action.gameId };
    case "upsertGame":
      return {
        ...state,
        games: { ...state.games, [action.game.game_id]: action.game },
      };
    case "upsertTeam": {
      const teams = state.teamsByGame[action.team.game_id] || [];
      const existingIndex = teams.findIndex((team) => team.team_id === action.team.team_id);
      const updatedTeams = existingIndex >= 0 ? [...teams] : [...teams, action.team];
      if (existingIndex >= 0) {
        updatedTeams[existingIndex] = action.team;
      }
      return {
        ...state,
        teamsByGame: {
          ...state.teamsByGame,
          [action.team.game_id]: updatedTeams,
        },
      };
    }
    case "mergePlayers": {
      const { playersById, playerIdsByTeam } = mergePlayersIntoState(state, action.players);
      return { ...state, playersById, playerIdsByTeam };
    }
    case "hydrateGame":
      return hydrateGameState(state, action.payload);
    case "upsertMessage": {
      const current = state.messagesByGame[action.gameId] || [];
      const filtered = current.filter((msg) => msg.message_id !== action.message.message_id);
      const updated = sortByTimestampDescending([...filtered, action.message], (msg) => msg.timestamp);
      return {
        ...state,
        messagesByGame: {
          ...state.messagesByGame,
          [action.gameId]: updated,
        },
      };
    }
    case "upsertAction": {
      const current = state.actionsByGame[action.gameId] || [];
      const filtered = current.filter((act) => act.action_id !== action.action.action_id);
      const updated = sortByTimestampDescending([...filtered, action.action], (act) => act.timestamp);
      return {
        ...state,
        actionsByGame: {
          ...state.actionsByGame,
          [action.gameId]: updated,
        },
      };
    }
    case "setPlayerActionCount": {
      const player = state.playersById[action.playerId];
      if (!player) {
        return state;
      }
      return {
        ...state,
        playersById: {
          ...state.playersById,
          [action.playerId]: { ...player, action_count: action.actionCount },
        },
        playerActionCounts: { ...state.playerActionCounts, [action.playerId]: action.actionCount },
      };
    }
    case "setPlayerActionCounts":
      return {
        ...state,
        playerActionCounts: { ...state.playerActionCounts, ...action.counts },
      };
    case "setPlayerCharCounts":
      return {
        ...state,
        playerCharCounts: { ...state.playerCharCounts, ...action.counts },
      };
    case "updatePlayerLandingSiteInvestments":
      return {
        ...state,
        playerLandingSiteInvestments: {
          ...state.playerLandingSiteInvestments,
          [action.playerId]: {
            total_points: action.total_points,
            investments: action.investments,
          },
        },
      };
    default:
      return state;
  }
}

interface PlayerApiResponse {
  player_id: string;
  team_id: string;
  login_code: string;
  role: string;
  comm_lvl: number | null;
  scan_lvl: number | null;
  ship_dmg?: number | null;
  personal_score?: number | null;
  team_name: string;
  team_code_name: string;
  game_name: string;
  shields_on?: boolean;
  landing_gear?: string;
  current_location?: string | null;
  action_count?: number;
}

function mapPlayersResponse(players: PlayerApiResponse[]): AdminPlayer[] {
  return players.map((player) => ({
    player_id: player.player_id,
    team_id: player.team_id,
    login_code: player.login_code,
    role: player.role,
    comm_lvl: player.comm_lvl ?? null,
    scan_lvl: player.scan_lvl ?? null,
    ship_dmg: player.ship_dmg ?? null,
    personal_score: player.personal_score ?? 0,
    team_name: player.team_name,
    team_code_name: player.team_code_name,
    game_name: player.game_name,
    shields_on: Boolean(player.shields_on),
    landing_gear: player.landing_gear ?? "",
    current_location: player.current_location ?? null,
    action_count: player.action_count ?? 0,
  }));
}

const createAdminFields: CreateEntityField[] = [
  { name: "user_name", label: "Admin Username", placeholder: "Enter username" },
  { name: "password", label: "Password", type: "password", placeholder: "Enter password" },
];

export default function AdminActions({ adminId, onActiveGameChange, onLogout }: AdminActionsProps) {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const [gameOptions, setGameOptions] = useState<AdminGame[]>([]);
  const [isSelectModalOpen, setSelectModalOpen] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<string>("");
  const [activePanel, setActivePanel] = useState<PanelView>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasUnreadActions, setHasUnreadActions] = useState<boolean>(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteGameId, setSelectedDeleteGameId] = useState<string>("");
  const [deleteConfirmationText, setDeleteConfirmationText] = useState<string>("");
  
  // Use ref to track if actions view is currently open in GameCard
  const isViewingActionsRef = useRef<boolean>(false);
  const wsRef = useRef<WebSocket | null>(null);
  const handleAdminEventRef = useRef<(data: any) => void>(() => {});

  // Notify parent when active game changes
  useEffect(() => {
    if (onActiveGameChange) {
      onActiveGameChange(state.activeGameId);
    }
  }, [state.activeGameId, onActiveGameChange]);

  const activeGame = state.activeGameId ? state.games[state.activeGameId] : null;
  const teamsForActiveGame = activeGame ? (state.teamsByGame[activeGame.game_id] || []) : [];
  const messagesForGame = activeGame ? state.messagesByGame[activeGame.game_id] : undefined;
  const actionsForGame = activeGame ? state.actionsByGame[activeGame.game_id] : undefined;

  const playersByTeam = useMemo(() => {
    const map: Record<string, AdminPlayer[]> = {};
    Object.entries(state.playerIdsByTeam).forEach(([teamId, playerIds]) => {
      map[teamId] = playerIds
        .map((playerId) => state.playersById[playerId])
        .filter((player): player is AdminPlayer => Boolean(player));
    });
    return map;
  }, [state.playerIdsByTeam, state.playersById]);

  const availableGameOptions = useMemo(() => {
    const byId: Record<string, AdminGame> = {};
    gameOptions.forEach((game) => {
      byId[game.game_id] = game;
    });
    Object.values(state.games).forEach((game) => {
      byId[game.game_id] = game;
    });
    return Object.values(byId).sort((a, b) => a.game_name.localeCompare(b.game_name));
  }, [gameOptions, state.games]);

  const parseJson = useCallback(
    async <T,>(response: Response, fallback: string, defaultValue?: T): Promise<T> => {
      if (response.ok) {
        return response.json();
      }

      if (response.status === 404 && defaultValue !== undefined) {
        return defaultValue;
      }

      let message = fallback;
      try {
        const body = await response.json();
        if (body?.detail) {
          message = body.detail;
        }
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    },
    []
  );

  // Cache utilities for game list
  const GAME_LIST_CACHE_KEY = "lost-in-space-game-list";
  const GAME_LIST_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  const getCachedGameList = useCallback((): AdminGame[] | null => {
    try {
      const cached = localStorage.getItem(GAME_LIST_CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp > GAME_LIST_CACHE_TTL) {
        localStorage.removeItem(GAME_LIST_CACHE_KEY);
        return null;
      }

      return data as AdminGame[];
    } catch (err) {
      console.error("Error reading game list cache:", err);
      localStorage.removeItem(GAME_LIST_CACHE_KEY);
      return null;
    }
  }, [GAME_LIST_CACHE_TTL]);

  const setCachedGameList = useCallback((games: AdminGame[]) => {
    try {
      const cacheData = {
        data: games,
        timestamp: Date.now()
      };
      localStorage.setItem(GAME_LIST_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing game list cache:", err);
    }
  }, []);

  const invalidateGameListCache = () => {
    localStorage.removeItem(GAME_LIST_CACHE_KEY);
  };

  const fetchGameList = useCallback(async (forceRefresh: boolean = false) => {
    // Check cache first unless force refresh is requested
    if (!forceRefresh) {
      const cachedGames = getCachedGameList();
      if (cachedGames) {
        console.log("Using cached game list");
        setGameOptions(cachedGames);
        dispatch({ type: "setGameList", games: cachedGames });
        return;
      }
    }

    // Fetch from API
    try {
      const res = await authenticatedFetch("/games/");
      const data = await parseJson<AdminGame[]>(res, "Failed to load games", []);
      
      // Update cache
      setCachedGameList(data);
      
      setGameOptions(data);
      dispatch({ type: "setGameList", games: data });
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  }, [parseJson, getCachedGameList, setCachedGameList]);

  useEffect(() => {
    fetchGameList();
  }, [fetchGameList]);

  const loadGameData = useCallback(
    async (gameId: string) => {
      if (!gameId) return;

      setIsLoading(true);
      setError("");
      try {
        // Use consolidated endpoint to get all game data in one request
        const fullDataRes = await authenticatedFetch(`/games/full-data?game_id=${encodeURIComponent(gameId)}`);
        const fullData = await parseJson<any>(fullDataRes, "Failed to load game data");
        
        // Extract data from consolidated response
        const game = fullData.game;
        const teams = fullData.teams || [];
        const playersRaw = fullData.players || [];
        const messages = fullData.messages || [];
        const actions = fullData.actions || [];
        const playerActionCounts = fullData.player_action_counts || {};
        const playerCharCounts = fullData.player_char_counts || {};
        const playerLandingSiteInvestments = fullData.player_landing_site_investments || {};

        const players = mapPlayersResponse(playersRaw);

        // Track the current turn for this game
        previousTurnsRef.current[game.game_id] = game.current_turn ?? null;
        
        dispatch({
          type: "hydrateGame",
          payload: {
            game,
            teams,
            players,
            messages,
            actions,
            playerActionCounts,
            playerCharCounts,
            playerLandingSiteInvestments,
          },
        });

        setGameOptions((prev) => {
          const exists = prev.some((g) => g.game_id === game.game_id);
          if (exists) {
            return prev.map((g) => (g.game_id === game.game_id ? game : g));
          }
          return [...prev, game];
        });

        setSelectedGameId(game.game_id);
        setActivePanel(null);
        setSelectModalOpen(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load game data");
      } finally {
        setIsLoading(false);
      }
    },
    [parseJson]
  );

  // Track previous turn for each game to detect turn changes
  const previousTurnsRef = useRef<Record<string, number | null>>({});
  const stateRef = useRef(state);
  stateRef.current = state;

  const handleAdminEvent = useCallback(
    (data: any) => {
      const currentGameId = stateRef.current.activeGameId;
      if (data.type === "game_update" && data.game) {
        console.log("handleAdminEvent for game update", data.game, data);
        const previousTurn = previousTurnsRef.current[data.game.game_id] ?? null;
        const currentTurn = data.game.current_turn ?? null;
        
        // Check if turn changed
        if (previousTurn !== null && currentTurn !== null && previousTurn !== currentTurn) {
          console.log(`Turn changed from ${previousTurn} to ${currentTurn} for game ${data.game.game_id}`);
          // Refetch character counts for all players in this game when turn changes
          if (currentGameId === data.game.game_id) {
            const fetchCharCountsForTurn = async () => {
              try {
                const currentState = stateRef.current;
                const teams = currentState.teamsByGame[data.game.game_id] || [];
                const allPlayerIds: string[] = [];
                teams.forEach(team => {
                  const teamPlayerIds = currentState.playerIdsByTeam[team.team_id] || [];
                  allPlayerIds.push(...teamPlayerIds);
                });
                
                // Batch fetch character counts for all players
                const charCountPromises = allPlayerIds.map(async (playerId) => {
                  try {
                    const res = await authenticatedFetch(
                      `/messages/chars-sent-this-turn?player_id=${encodeURIComponent(playerId)}`
                    );
                    if (res.ok) {
                      const data = await res.json();
                      return { playerId, count: data.chars_sent ?? 0 };
                    }
                  } catch (err) {
                    console.error(`Failed to fetch char count for player ${playerId}:`, err);
                  }
                  return { playerId, count: 0 };
                });
                
                const results = await Promise.all(charCountPromises);
                const charCounts: Record<string, number> = {};
                results.forEach(({ playerId, count }) => {
                  charCounts[playerId] = count;
                });
                
                dispatch({ type: "setPlayerCharCounts", counts: charCounts });
              } catch (err) {
                console.error("Failed to refetch character counts on turn change:", err);
              }
            };
            fetchCharCountsForTurn();
          }
        }
        
        // Update previous turn tracking
        previousTurnsRef.current[data.game.game_id] = currentTurn;
        
        dispatch({ type: "upsertGame", game: data.game });
        if (Array.isArray(data.players) && data.players.length > 0) {
          dispatch({ type: "mergePlayers", players: data.players });
        }
      } else if (data.type === "team_update") {
        if (data.team) {
          dispatch({ type: "upsertTeam", team: data.team });
        }
        if (Array.isArray(data.players) && data.players.length > 0) {
          dispatch({ type: "mergePlayers", players: data.players });
        }
      } else if (data.type === "player_update" && data.player) {
        dispatch({ type: "mergePlayers", players: [data.player] });
      } else if (data.type === "message_update" && data.message && currentGameId) {
        console.log("handleAdminEvent for message update", data);
        dispatch({ type: "upsertMessage", gameId: currentGameId, message: data.message });
        const playersToMerge: AdminPlayer[] = [];
        if (data.player_sender) {
          playersToMerge.push(data.player_sender);
          // Update character counts when messages are sent
          if (typeof data.chars_sent_this_turn === "number") {
            dispatch({
              type: "setPlayerCharCounts",
              counts: { [data.player_sender.player_id]: data.chars_sent_this_turn }
            });
          }
        }
        if (data.player_receiver) {
          playersToMerge.push(data.player_receiver);
        }
        if (playersToMerge.length) {
          dispatch({ type: "mergePlayers", players: playersToMerge });
        }
        // Dispatch custom event for message updates to refresh character counts
        window.dispatchEvent(new CustomEvent('newMessage'));
      } else if (data.type === "action_update" && data.action && currentGameId) {
        console.log("handleAdminEvent for action update", data.action, data.player);
        // Set notification badge for actions (only if not currently viewing actions)
        if (!isViewingActionsRef.current) {
          setHasUnreadActions(true);
        }
        dispatch({ type: "upsertAction", gameId: currentGameId, action: data.action });
        if (typeof data.action_count === "number" && data.action.player_id) {
          dispatch({
            type: "setPlayerActionCount",
            playerId: data.action.player_id,
            actionCount: data.action_count,
          });
        }
        if (data.player) {
          console.log("handleAdminEvent for player update", data.player);
          const playerPayload = {
            ...data.player,
            ...(typeof data.action_count === "number" ? { action_count: data.action_count } : {}),
          };
          dispatch({ type: "mergePlayers", players: [playerPayload] });
        }
      } else if (data.type === "landing_site_update" && data.player) {
        console.log("handleAdminEvent for landing site update", data.player, data.total_points, data.investments);
        // Update the player with landing site points information
        const playerPayload = {
          ...data.player,
          landing_site_points: data.total_points,
          landing_site_investments: Array.isArray(data.investments) ? data.investments : undefined,
        };
        dispatch({ type: "mergePlayers", players: [playerPayload] });
        
        // Also update the playerLandingSiteInvestments state if we have the target player ID
        const targetPlayerId = data.target_player_id || data.player.player_id;
        if (targetPlayerId && typeof data.total_points === "number" && Array.isArray(data.investments)) {
          dispatch({
            type: "updatePlayerLandingSiteInvestments",
            playerId: targetPlayerId,
            total_points: data.total_points,
            investments: data.investments,
          });
        }
      } else if (data.type === "timer_update") {
        // Dispatch timer update event for TimerContext to handle
        console.log("Admin: Received timer_update event", data);
        window.dispatchEvent(new CustomEvent("timerUpdate", { 
          detail: { 
            timer_seconds: data.timer_seconds, 
            action: data.action,
            server_time: data.server_time,
            end_time: data.end_time,
            additional_seconds: data.additional_seconds,
            is_paused: data.is_paused
          } 
        }));
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch] // state.activeGameId is accessed via stateRef.current inside the callback
  );

  // Keep the ref updated with the latest handler
  useEffect(() => {
    handleAdminEventRef.current = handleAdminEvent;
  }, [handleAdminEvent]);

  useEffect(() => {
    if (!adminId || !state.activeGameId) {
      console.log("Admin WebSocket: missing adminId or activeGameId", { adminId, activeGameId: state.activeGameId });
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectAttempts = 0;
    let reconnectTimeoutId: NodeJS.Timeout | null = null;
    let heartbeatIntervalId: NodeJS.Timeout | null = null;
    let healthCheckIntervalId: NodeJS.Timeout | null = null;
    let lastPongReceived = Date.now();
    let isIntentionallyClosing = false;
    const MAX_RECONNECT_ATTEMPTS = 10;
    const HEARTBEAT_INTERVAL = 25000; // 25 seconds (slightly less than server's 30s)
    const HEALTH_CHECK_INTERVAL = 5000; // Check health every 5 seconds
    const PONG_TIMEOUT = 90000; // 90 seconds without pong = dead connection

    const connectWebSocket = () => {
      if (isIntentionallyClosing) return;

      // Close existing connection if any
      if (wsRef.current) {
        console.log("Admin WebSocket: closing existing connection");
        wsRef.current.close();
        wsRef.current = null;
      }

      // Convert HTTP API URL to WebSocket URL
      const apiUrl = new URL(API_BASE_URL);
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiUrl.host}/ws/admin/${adminId}/${state.activeGameId}`;
      console.log("Admin WebSocket: connecting to", wsUrl);
      socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        console.log("Admin WebSocket: connection opened");
        reconnectAttempts = 0; // Reset on successful connection
        lastPongReceived = Date.now();
        
        try {
          socket!.send(JSON.stringify({ type: "ping" }));
        } catch (err) {
          console.warn("Admin WS ping failed", err);
        }

        // Start periodic heartbeat
        heartbeatIntervalId = setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            try {
              socket.send(JSON.stringify({ type: "ping" }));
              console.debug("Admin: Sent heartbeat ping");
            } catch (e) {
              console.warn("Admin: Failed to send heartbeat ping:", e);
            }
          }
        }, HEARTBEAT_INTERVAL);

        // Start health check
        healthCheckIntervalId = setInterval(() => {
          const timeSinceLastPong = Date.now() - lastPongReceived;
          if (timeSinceLastPong > PONG_TIMEOUT) {
            console.warn(`Admin: No pong received for ${timeSinceLastPong}ms, connection may be dead. Reconnecting...`);
            if (socket) {
              socket.close();
            }
          }
        }, HEALTH_CHECK_INTERVAL);
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          
          // Handle pong responses
          if (payload.type === "pong") {
            lastPongReceived = Date.now();
            console.debug("Admin: Received pong from server");
            return;
          }
          
          // Handle ping from server (respond with pong)
          if (payload.type === "ping") {
            if (socket && socket.readyState === WebSocket.OPEN) {
              try {
                socket.send(JSON.stringify({ type: "pong" }));
              } catch (e) {
                console.warn("Admin: Failed to send pong:", e);
              }
            }
            return;
          }
          
          console.log("Admin WebSocket message received:", payload);
          handleAdminEventRef.current(payload);
        } catch (err) {
          console.error("Failed to parse admin WS message", err);
        }
      };

      socket.onerror = (event) => {
        console.error("Admin WebSocket error", event);
      };

      socket.onclose = (event) => {
        console.log("Admin WebSocket: connection closed", { code: event.code, reason: event.reason, wasClean: event.wasClean });
        if (wsRef.current === socket) {
          wsRef.current = null;
        }

        // Clear intervals
        if (heartbeatIntervalId) {
          clearInterval(heartbeatIntervalId);
          heartbeatIntervalId = null;
        }
        if (healthCheckIntervalId) {
          clearInterval(healthCheckIntervalId);
          healthCheckIntervalId = null;
        }

        // Attempt reconnection if not intentionally closing
        if (!isIntentionallyClosing && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000);
          console.log(`Admin: Reconnecting WebSocket in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutId = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.error("Admin: Max reconnection attempts reached. Please refresh the page.");
        }
      };
    };

    // Initial connection
    connectWebSocket();

    return () => {
      isIntentionallyClosing = true;
      
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
      }
      if (healthCheckIntervalId) {
        clearInterval(healthCheckIntervalId);
      }
      if (socket) {
        socket.close();
      }
      if (wsRef.current === socket) {
        wsRef.current = null;
      }
    };
  }, [adminId, state.activeGameId]);

  const handleCreateAdminSubmit = useCallback(async (values: Record<string, string>) => {
    const userName = values.user_name?.trim();
    const password = values.password?.trim();

    if (!userName || !password) {
      throw new Error("Username and password are required");
    }

    const res = await authenticatedFetch("/admins/", {
      method: "POST",
      body: JSON.stringify({ user_name: userName, password }),
    });

    const data = await parseJson<{ user_name: string }>(res, "Failed to create admin");
    return { successMessage: `Admin "${data.user_name}" created successfully!` };
  }, [parseJson]);

  const handleGameUpdate = useCallback((updatedGame: AdminGame) => {
    dispatch({ type: "upsertGame", game: updatedGame });
    // Invalidate cache when a game is updated
    invalidateGameListCache();
    // Update game options list
    setGameOptions((prev) => {
      const exists = prev.some((g) => g.game_id === updatedGame.game_id);
      if (exists) {
        return prev.map((g) => (g.game_id === updatedGame.game_id ? updatedGame : g));
      }
      return [...prev, updatedGame];
    });
  }, []);

  const openGameSelector = () => {
    // Always fetch games when opening the modal to ensure we have the latest list
    fetchGameList(true); // Force refresh to get latest games
    const defaultId = state.activeGameId || availableGameOptions[0]?.game_id || "";
    setSelectedGameId(defaultId);
    setSelectModalOpen(true);
  };

  const renderActivePanel = () => {
    if (activePanel === "createGame") {
      return (
        <div style={{ padding: "1rem", backgroundColor: "white", borderRadius: "8px", border: "1px solid #dee2e6" }}>
          <CreateGameForm
            onClose={() => setActivePanel(null)}
            onSuccess={(game) => {
              dispatch({ type: "upsertGame", game });
              setGameOptions((prev) => {
                const exists = prev.some((g) => g.game_id === game.game_id);
                if (exists) {
                  return prev.map((g) => (g.game_id === game.game_id ? game : g));
                }
                return [...prev, game];
              });
              // Invalidate cache when a new game is created
              invalidateGameListCache();
            }}
          />
        </div>
      );
    }

    if (activePanel === "createAdmin") {
      return (
        <div style={{ padding: "1rem", backgroundColor: "white", borderRadius: "8px", border: "1px solid #dee2e6" }}>
          <CreateEntity
            title="Create Admin"
            fields={createAdminFields}
            onSubmit={handleCreateAdminSubmit}
            onClose={() => setActivePanel(null)}
            submitLabel="Create Admin"
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <ActionPanel
        leftButtons={[
          {
            label: "Create Game",
            onClick: () => setActivePanel((prev) => (prev === "createGame" ? null : "createGame")),
          },
          {
            label: "View Game",
            onClick: openGameSelector,
            backgroundColor: "#20c997",
          },
          {
            label: "Delete Game",
            onClick: () => {
              fetchGameList(true); // Force refresh to get latest games
              setSelectedDeleteGameId(availableGameOptions[0]?.game_id || "");
              setDeleteConfirmationText("");
              setDeleteModalOpen(true);
            },
            backgroundColor: "#dc3545",
          },
        ]}
        rightButtons={[
          {
            label: "Refresh Games",
            onClick: () => {
              invalidateGameListCache();
              fetchGameList(true); // Force refresh
            },
            backgroundColor: "#17a2b8",
          },
          {
            label: "Create Admin",
            onClick: () => setActivePanel((prev) => (prev === "createAdmin" ? null : "createAdmin")),
            backgroundColor: "#6f42c1",
          },
          ...(onLogout ? [{
            label: "Logout",
            onClick: onLogout,
            backgroundColor: "#dc3545",
          }] : []),
        ]}
        activeContent={renderActivePanel()}
      />

      {isLoading && (
        <p style={{ color: "#6c757d", marginTop: "1rem" }}>
          Loading game data...
        </p>
      )}

      {error && (
        <div style={{ 
          color: "#721c24", 
          backgroundColor: "#f8d7da", 
          padding: "0.75rem", 
          borderRadius: "4px",
          border: "1px solid #f5c6cb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem"
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            style={{
              background: "none",
              border: "none",
              color: "#721c24",
              cursor: "pointer",
              fontSize: "1.2rem",
              fontWeight: "bold",
              padding: "0",
              minWidth: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Close"
          >
            ×
          </button>
        </div>
      )}

      <div style={{ marginTop: "2rem" }}>
        {activeGame ? (
          <GameCard
            game={activeGame}
            onUpdate={handleGameUpdate}
            teams={teamsForActiveGame}
            playersByTeam={playersByTeam}
            messages={messagesForGame}
            actions={actionsForGame}
            playerActionCounts={state.playerActionCounts}
            playerCharCounts={state.playerCharCounts}
            playerLandingSiteInvestments={state.playerLandingSiteInvestments}
            hasUnreadActions={hasUnreadActions}
            onClearActionsNotification={() => setHasUnreadActions(false)}
            onViewingActionsChange={(isViewing) => {
              isViewingActionsRef.current = isViewing;
              // Clear notification when viewing actions
              if (isViewing) {
                setHasUnreadActions(false);
              }
            }}
          />
        ) : (
          <div
            style={{
              padding: "1.5rem",
              border: "1px dashed #ced4da",
              borderRadius: "8px",
              textAlign: "center",
              color: "#6c757d",
            }}
          >
            Select a game to view its details.
          </div>
        )}
      </div>

      <ActionModal
        isOpen={isSelectModalOpen}
        onClose={() => setSelectModalOpen(false)}
        title="Select Game"
      >
        {availableGameOptions.length === 0 ? (
          <p style={{ color: "#6c757d" }}>No games available. Create one first.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <label>
              Game
              <select
                value={selectedGameId}
                onChange={(e) => setSelectedGameId(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "0.5rem",
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              >
                <option value="" disabled>
                  Select a game
                </option>
                {availableGameOptions.map((game) => (
                  <option key={game.game_id} value={game.game_id}>
                    {game.game_name}
                  </option>
                ))}
              </select>
            </label>
            <button
              onClick={() => loadGameData(selectedGameId)}
              disabled={!selectedGameId || isLoading}
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                cursor: !selectedGameId || isLoading ? "not-allowed" : "pointer",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                opacity: !selectedGameId || isLoading ? 0.6 : 1,
              }}
            >
              View Game
            </button>
          </div>
        )}
      </ActionModal>

      <ActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteConfirmationText("");
          setSelectedDeleteGameId("");
        }}
        title="Delete Game"
      >
        {(() => {
          // Filter out games with ACTIVE status
          const deletableGames = availableGameOptions.filter(game => game.game_status !== "ACTIVE");
          return deletableGames.length === 0 ? (
            <p style={{ color: "#6c757d" }}>No games available to delete. Active games cannot be deleted.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <label>
                Game
                <select
                  value={selectedDeleteGameId}
                  onChange={(e) => {
                    setSelectedDeleteGameId(e.target.value);
                    setDeleteConfirmationText(""); // Reset confirmation text when game changes
                  }}
                  style={{
                    width: "100%",
                    marginTop: "0.5rem",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                >
                  <option value="" disabled>
                    Select a game to delete
                  </option>
                  {deletableGames.map((game) => (
                    <option key={game.game_id} value={game.game_id}>
                      {game.game_name}
                    </option>
                  ))}
                </select>
              </label>
            {selectedDeleteGameId && (
              <>
                <label>
                  Type the name of the game to delete
                  <input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder={deletableGames.find(g => g.game_id === selectedDeleteGameId)?.game_name || ""}
                    style={{
                      width: "100%",
                      marginTop: "0.5rem",
                      padding: "0.5rem",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                    }}
                  />
                </label>
                <button
                  onClick={async () => {
                    const deletableGamesList = availableGameOptions.filter(game => game.game_status !== "ACTIVE");
                    const selectedGame = deletableGamesList.find(g => g.game_id === selectedDeleteGameId);
                    if (!selectedGame) {
                      setError("Please select a game");
                      return;
                    }
                    
                    if (deleteConfirmationText !== selectedGame.game_name) {
                      setError("Game name does not match. Please type the exact game name (case sensitive).");
                      return;
                    }
                    
                    setIsLoading(true);
                    setError("");
                    
                    try {
                      const res = await authenticatedFetch(`/games/${encodeURIComponent(selectedDeleteGameId)}`, {
                        method: "DELETE",
                      });
                      
                      if (!res.ok) {
                        const errorData = await res.json().catch(() => ({ detail: "Failed to delete game" }));
                        throw new Error(errorData.detail || "Failed to delete game");
                      }
                      
                      await res.json();
                      
                      // Remove game from state
                      dispatch({ type: "setGameList", games: availableGameOptions.filter(g => g.game_id !== selectedDeleteGameId) });
                      setGameOptions(prev => prev.filter(g => g.game_id !== selectedDeleteGameId));
                      
                      // If the deleted game was the active game, clear it
                      if (state.activeGameId === selectedDeleteGameId) {
                        dispatch({ type: "setActiveGame", gameId: null });
                      }
                      
                      // Invalidate cache
                      invalidateGameListCache();
                      
                      // Close modal and show success
                      setDeleteModalOpen(false);
                      setDeleteConfirmationText("");
                      setSelectedDeleteGameId("");
                      setError("");
                      // You could show a success message here if needed
                    } catch (err) {
                      console.error("Error deleting game:", err);
                      if (err instanceof Error) {
                        setError(err.message);
                      } else {
                        setError("Failed to delete game. Please try again.");
                      }
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                  disabled={!selectedDeleteGameId || isLoading || deleteConfirmationText !== (deletableGames.find(g => g.game_id === selectedDeleteGameId)?.game_name || "")}
                  style={{
                    padding: "0.75rem 1.5rem",
                    fontSize: "1rem",
                    cursor: (!selectedDeleteGameId || isLoading || deleteConfirmationText !== (deletableGames.find(g => g.game_id === selectedDeleteGameId)?.game_name || "")) ? "not-allowed" : "pointer",
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    opacity: (!selectedDeleteGameId || isLoading || deleteConfirmationText !== (deletableGames.find(g => g.game_id === selectedDeleteGameId)?.game_name || "")) ? 0.6 : 1,
                  }}
                >
                  Delete Game
                </button>
              </>
            )}
          </div>
          );
        })()}
      </ActionModal>
    </div>
  );
}

